import 'intl';
import 'intl/locale-data/jsonp/nl-NL';
import 'moment/locale/nl';

import {StatusBar} from 'expo-status-bar';
import {useState, useEffect, useContext, useCallback} from 'react';
import {View, ActivityIndicator} from 'react-native';
import {NavigationContainer} from '@react-navigation/native';
import {createStackNavigator} from '@react-navigation/stack';
import * as Linking from 'expo-linking';
import {
  configureFonts,
  MD2LightTheme as DefaultTheme,
  Provider as PaperProvider,
} from 'react-native-paper';

import moment from 'moment';

import {
  useFonts,
  Roboto_100Thin,
  Roboto_300Light,
  Roboto_400Regular,
  Roboto_500Medium,
  Roboto_700Bold,
  Roboto_900Black,
} from '@expo-google-fonts/roboto';

import configuration from './configuration';
import {user as userApi} from './api';
import {navigationRef} from './lib/root-navigation';
import authentication from './lib/authentication';
import metrics from './lib/metrics';
import Notifications from './lib/notifications';
import {useDispatch, useSelector} from './lib/hooks';
import {routeChange, setAppDoneLoading, hideLogoutDialog} from './actions';
import sy from './styles';

import AuthContext from './components/context/AuthContext';
import UserContext from './components/context/UserContext';
import {Modal, Text, Dialog} from './components/controls';
import {SideDrawer} from './components/shared';
import withSessionState from './components/hoc/with-session-state';

import {TabsFlow} from './flows';
import RequestScreen from './screens/request';
import ReplacementVehicleScreen from './screens/replacement_vehicle';
import SignInScreen from './screens/signin';

import LogRocket from 'logrocket';

LogRocket.init(configuration.LOG_ROCKET_KEY, {});

moment.locale('nl');

const withAuthContext = (App) => {
  return (props) => {
    const [auth, setAuth] = useState({authenticated: null});

    useEffect(() => {
      const check = async () => {
        const accessToken = await authentication.getAccessToken();
        setAuth({authenticated: !!accessToken});
      };

      authentication.addListener(async () => {
        await check();
      });

      check();
    }, []);

    return (
      <AuthContext.Provider value={auth}>
        <App {...props} />
      </AuthContext.Provider>
    );
  };
};

const withUserContext = (App) => {
  return (props) => {
    const dispatch = useDispatch();
    const {authenticated} = useContext(AuthContext);
    const [user, setUser] = useState(null);

    const fetch = useCallback(async () => {
      if (!authenticated) {
        return;
      }

      try {
        const me = await userApi.me();

        if (!me) {
          throw new Error('Not able to retrieve user from api.');
        }

        setUser(me);

        LogRocket.identify(me.user_id, me.user_settings.logrocket);
      } catch (e) {
        await authentication.logout();
        dispatch(setAppDoneLoading());
      }
    }, [authenticated, dispatch]);

    useEffect(() => {
      fetch();
    }, [fetch]);

    return (
      <UserContext.Provider value={{me: user, update: fetch}}>
        <App {...props} />
      </UserContext.Provider>
    );
  };
};

const prefix = Linking.makeUrl('/');
const routes = {
  screens: {
    Tabs: {
      path: '',
      screens: {
        Search: 'Search',
      },
    },
    Request: 'Request/:rob_request_id',
    ReplacementVehicle: 'ReplacementVehicle/:rob_request_id',
    Chat: 'Chat/:rob_request_id',
  },
};

const linking = {
  prefixes: [prefix],
  config: routes,
};

const theme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    primary: '#231fda',
    background: '#ffffff',
    text: '#49494a',
  },
  fonts: configureFonts({
    config: {
      web: {
        regular: {
          fontFamily: 'Roboto_400Regular',
          fontWeight: '400',
        },
        medium: {
          fontFamily: 'Roboto_500Medium',
          fontWeight: '500',
        },
      },
      native: {
        regular: {
          fontFamily: 'Roboto_400Regular',
          fontWeight: '400',
        },
        medium: {
          fontFamily: 'Roboto_500Medium',
          fontWeight: '500',
        },
      },
    },
    isV3: false,
  }),
};

const Stack = createStackNavigator();

const App = withAuthContext(
  withUserContext(
    withSessionState(() => {
      const dispatch = useDispatch();
      const {me} = useContext(UserContext);

      const loading = useSelector((state) => state.app_loading);
      const show_logout_dialog = useSelector(
        (state) => state.show_logout_dialog,
      );
      const show_drawer = useSelector((state) => state.show_drawer);

      const update_after = useSelector((state) => state.update_after);

      const [fontsLoaded] = useFonts({
        Roboto_100Thin,
        Roboto_300Light,
        Roboto_400Regular,
        Roboto_500Medium,
        Roboto_700Bold,
        Roboto_900Black,
      });

      useEffect(() => {
        Notifications.initialize();

        return () => {
          Notifications.kill();
        };
      }, []);

      useEffect(() => {
        if (update_after > 0) {
          const timeout = setTimeout(() => {
            window.location.reload();
          }, update_after - new Date().getTime());

          return () => clearTimeout(timeout);
        }
      }, [update_after]);

      useEffect(() => {
        if (fontsLoaded) {
          window.electronApi?.appLoaded();
        }
      }, [fontsLoaded]);

      useEffect(() => {
        const interval = setInterval(() => {
          const summary = metrics.summary();
          userApi.metrics(summary);
        }, 60 * 1000);

        return () => clearInterval(interval);
      });

      if (!fontsLoaded) {
        return <View />;
      }

      return (
        <>
          <PaperProvider theme={theme}>
            <StatusBar />
            <NavigationContainer
              ref={navigationRef}
              linking={linking}
              fallback={<Text>Loading...</Text>}
              documentTitle={{
                formatter: () => `Bahnkick Review`,
              }}
              onStateChange={(state) => {
                const {routes} = state;
                if (routes && routes.length) {
                  const route = routes[routes.length - 1];
                  dispatch(
                    routeChange({
                      name: route.name,
                      params: route.params,
                    }),
                  );
                }
              }}>
              <Stack.Navigator
                initialRouteName="Tabs"
                screenOptions={{
                  headerShown: false,
                  cardStyle: {
                    backgroundColor: 'rgba(74, 74, 72, 0.5)',
                    height: '100%',
                  },
                }}>
                <Stack.Screen
                  navigationKey={me?.user_id}
                  name="Tabs"
                  component={TabsFlow}
                  initialParams={{
                    initialRouteName: 'Search',
                  }}
                />
                <Stack.Screen name="Request" component={RequestScreen} />
                <Stack.Screen
                  name="ReplacementVehicle"
                  component={ReplacementVehicleScreen}
                />
                <Stack.Screen name="SignIn" component={SignInScreen} />
              </Stack.Navigator>
            </NavigationContainer>
            <>
              {show_drawer && <SideDrawer />}
              <Dialog
                visible={show_logout_dialog}
                onDismiss={() => dispatch(hideLogoutDialog())}
                buttons={[
                  {
                    text: 'Annuleren',
                    onPress: () => dispatch(hideLogoutDialog()),
                  },
                  {
                    text: 'OK',
                    onPress: async () => {
                      await authentication.logout();
                      dispatch(hideLogoutDialog());
                    },
                  },
                ]}
                options={{noPaddingContent: false, hideDividers: true}}>
                <View style={[sy['flex-row']]}>
                  <Text style={[sy['p-4']]}>
                    Weet je zeker dat je wilt uitloggen?
                  </Text>
                </View>
              </Dialog>
              {loading && (
                <Modal
                  visible={true}
                  style={{backgroundColor: 'rgba(255, 255, 255, 0.5)'}}
                  contentContainerStyle={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'transparent',
                  }}>
                  <ActivityIndicator size="large" color="#231fda" />
                </Modal>
              )}
            </>
          </PaperProvider>
        </>
      );
    }),
  ),
);

export default App;
